let context: AudioContext | undefined;
const VOLUME = 0.01; // Square waves are loud
const FADE_TIME = 0.01;

function initAudioContext(): void {
  // Has to be init by user action
  context = new AudioContext();
  [...document.getElementsByClassName("to-hide")].forEach((element) => {
    element.setAttribute("hidden", "hidden");
  });
  [...document.getElementsByClassName("to-unhide")].forEach((element) => {
    element.removeAttribute("hidden");
  });
}

function sleep(length: number): Promise<void> {
  return new Promise((res) => setTimeout(res, length * 1000));
}

async function playNote(
  freq: number,
  length: number,
  fadeTime: number = FADE_TIME,
  volume: number = VOLUME,
): Promise<void> {
  if (!context) {
    throw new Error("AudioContext not initialized. Call initAudioContext() first.");
  }

  const oscilator = context.createOscillator();
  oscilator.type = "square";
  oscilator.frequency.setValueAtTime(freq, context.currentTime);

  const gainNode = context.createGain();
  gainNode.gain.setValueAtTime(volume, context.currentTime);

  oscilator.connect(gainNode);
  gainNode.connect(context.destination);

  oscilator.start();

  await sleep(length);

  // Fade the sound in a few ms to stop the "click" sound
  gainNode.gain.setValueAtTime(volume, context.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(
    volume / 1000,
    context.currentTime + fadeTime,
  );

  // Stop oscilator after the fade finished, but no waiting before returning from the function
  return new Promise(async (resolve) => {
    await sleep(fadeTime);
    oscilator.stop();
    resolve();
  });
}

// Overlaps several noted with slightly different frequencies
async function playWeirdNote(
  freq: number,
  length: number,
  fade_time: number = FADE_TIME,
  volume: number = VOLUME,
): Promise<void[]> {
  if (!context) {
    throw new Error("AudioContext not initialized. Call initAudioContext() first.");
  }

  const range = freq / 50;

  // Careful here. With volume, two 0.01 is a tiny bit more than 0.02. It adds up.
  const MEAN_NOTE_COUNT = 5;

  const frequencies: number[] = [];
  for (
    let diff = -range;
    diff < range;
    diff +=
    range / MEAN_NOTE_COUNT +
    Math.random() * ((2 * range) / MEAN_NOTE_COUNT)
  ) {
    frequencies.push(freq + diff);
  }

  return Promise.all(
    frequencies.map(async (offsetFrequency) => {
      const period = 1 / offsetFrequency;
      const offset = period * Math.random();
      // Random offset between 0 and 1 period, so that all the notes are not in phase at the start (the "tick" at the start)
      // Doesn't looks to be working, probably because all the sleeps exit in the same event loop :(
      await sleep(offset);
      return playNote(
        offsetFrequency,
        length - offset,
        fade_time,
        volume / frequencies.length,
      );
    }),
  );
}

async function motus(): Promise<void> {
  await playNote(987.77, 0.2);
  await sleep(0.02);
  await playNote(987.77, 0.2);
  await sleep(0.02);
  await playNote(987.77, 0.2);
  await sleep(0.02);
  await playNote(783.99, 0.2);
  await sleep(0.02);
  await playNote(783.99, 0.2);
  await sleep(0.02);
  await playNote(196.0, 0.2);
  await sleep(0.02);
  await playNote(783.99, 0.2);
  await sleep(0.02);
  await playNote(987.77, 0.2);
}

async function weirdMotus(): Promise<void> {
  await playWeirdNote(987.77, 0.2);
  await sleep(0.02);
  await playWeirdNote(987.77, 0.2);
  await sleep(0.02);
  await playWeirdNote(987.77, 0.2);
  await sleep(0.02);
  await playWeirdNote(783.99, 0.2);
  await sleep(0.02);
  await playWeirdNote(783.99, 0.2);
  await sleep(0.02);
  await playWeirdNote(196.0, 0.2);
  await sleep(0.02);
  await playWeirdNote(783.99, 0.2);
  await sleep(0.02);
  await playWeirdNote(987.77, 0.2);
}


interface MotusWindow extends Window {
  initAudioContext?: typeof initAudioContext;
  playNote?: typeof playNote;
  playWeirdNote?: typeof playWeirdNote;
  motus?: typeof motus;
  weirdMotus?: typeof weirdMotus;
}

const motusWindow = window as MotusWindow;
motusWindow.initAudioContext = initAudioContext;
motusWindow.playNote = playNote;
motusWindow.playWeirdNote = playWeirdNote;
motusWindow.motus = motus;
motusWindow.weirdMotus = weirdMotus;
